import { useEffect, useRef } from 'react';

export interface TriStateCheckboxProps {
  /** 전체 선택 여부. `mixed`가 켜져 있으면 무시된다(네이티브는 둘을 동시에 표현하지 못한다). */
  checked: boolean;
  /** 부분 선택. 네이티브 `indeterminate`로 표시한다. */
  mixed: boolean;
  /** 다음 상태. 부분 상태에서 눌렀을 때는 항상 `true`(전원 ON)로 온다. */
  onChange: (next: boolean) => void;
  /** 비활성. 잠금 그룹 등 실제 방어선이 여기인 자리가 있다. */
  disabled?: boolean;
  /** 라벨. `<label>`로 감싸지 않는 호출부(행 전체 토글을 브라우저에 맡기면 안 되는 경우)에 필요. */
  ariaLabel?: string;
  /** 추가 클래스. 기본 스타일을 두지 않으므로 치수·색은 호출부가 소유한다. */
  className?: string;
}

/**
 * tri-state 체크박스 — 부분 선택은 `indeterminate`로 표시한다
 * ([[feedback-multiselect-mixed-tristate-standard]]: 체크박스의 혼합 표현은 indeterminate).
 * 부분 상태에서 누르면 **전체 켜기**로 통일한다(같은 관례의 "클릭하면 전체 통일").
 *
 * `indeterminate`는 속성이 아니라 DOM 프로퍼티라 JSX로 못 준다 — ref로 매번 써 넣어야 하고,
 * 그 배선을 호출부마다 복제하면 `mixed`가 바뀌어도 표시가 안 따라오는 자리가 생긴다.
 * 그래서 로직만 여기로 모은다.
 *
 * **기본 스타일을 두지 않는다** — web(잠금·포커스 링까지 얹은 목록 체크박스)과 admin(속성창의
 * `h-3.5`/`h-4` 미니 체크박스)이 치수부터 다르고, 여기서 기본을 정하면 한쪽은 반드시 덮어써야
 * 한다. 표현은 호출부, 상태 규약은 이 컴포넌트가 갖는 분담이다.
 */
export function TriStateCheckbox({
  checked,
  mixed,
  onChange,
  disabled,
  ariaLabel,
  className,
}: TriStateCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  // 의존성 배열 없이 **매 렌더** 동기화한다. 체크박스를 누르면 브라우저가 활성화 동작으로 DOM
  // `indeterminate`를 스스로 끄는데(HTML 표준 — 클릭 전 단계에서 false), 호출부의 `mixed`가 그대로면
  // `[mixed]` 의존 효과는 재실행되지 않아 부분 표시가 사라진 채 남는다(2026-09-14). 프로퍼티 대입
  // 하나라 매 렌더 비용은 없다.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  });
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={ariaLabel}
      className={className}
      checked={mixed ? false : checked}
      disabled={disabled}
      // 부분 상태에서는 네이티브가 false→true를 주므로 그대로 전체 켜기가 된다.
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}
